import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Server, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRESS_TYPES = ["Flexographic", "Offset", "Gravure", "Digital", "Screen", "Letterpress", "Corrugated"];

const EMPTY = { name: "", press_type: "", manufacturer: "", model: "",
                web_width_inches: "", max_speed_fpm: "", stations: "",
                recommended_ink_systems: "", notes: "" };

export default function InkPresses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/ink/presses").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.press_type) { toast.error("Name and type required"); return; }
    setLoading(true);
    try {
      await api.post("/ink/presses", {
        ...form,
        web_width_inches: form.web_width_inches ? parseFloat(form.web_width_inches) : null,
        max_speed_fpm: form.max_speed_fpm ? parseFloat(form.max_speed_fpm) : null,
        stations: form.stations ? parseInt(form.stations, 10) : null,
        recommended_ink_systems: form.recommended_ink_systems
          ? form.recommended_ink_systems.split(",").map(s => s.trim()).filter(Boolean) : [],
      });
      toast.success("Press added");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this press?")) return;
    await api.delete(`/ink/presses/${id}`);
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
            <Link to="/app/ink" className="hover:text-primary">Ink &amp; Print</Link> / Presses
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="ink-presses-title">
            Press &amp; Equipment Database
          </h1>
          <p className="text-sm text-muted-foreground mt-2">Track every press you sell into. The Copilot uses this when generating recommendations.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="add-press-btn">
          <Plus className="w-4 h-4 mr-2" /> Add press
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <Server className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">No presses yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Add customer presses here. Once added, they show up in the Copilot dropdown for one-click case setup.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="border border-border bg-card p-5 rounded-sm" data-testid={`press-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg leading-tight">{p.name}</div>
                  <div className="text-xs font-mono-plex text-muted-foreground mt-0.5">
                    {p.manufacturer} {p.model}
                  </div>
                </div>
                <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-press-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3">
                <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-primary/10 text-primary hover:bg-primary/10">{p.press_type}</Badge>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                {p.web_width_inches != null && <div><span className="opacity-70">Web:</span> {p.web_width_inches}&quot;</div>}
                {p.stations != null && <div><span className="opacity-70">Stations:</span> {p.stations}</div>}
                {p.max_speed_fpm != null && <div className="col-span-2"><span className="opacity-70">Max:</span> {p.max_speed_fpm} fpm</div>}
              </div>
              {p.recommended_ink_systems?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="overline text-muted-foreground mb-1.5">Recommended inks</div>
                  <div className="flex flex-wrap gap-1">
                    {p.recommended_ink_systems.map((s, i) => (
                      <span key={i} className="text-[10px] font-mono-plex px-1.5 py-0.5 bg-secondary rounded-sm">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {p.notes && <p className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground leading-relaxed">{p.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="overline text-accent">◉ NEW PRESS</div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">Add press</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 pt-2">
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" placeholder="e.g. Acme Line 3" data-testid="press-name-input" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Type *</label>
                <Select value={form.press_type} onValueChange={(v) => setForm({ ...form, press_type: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="press-type-select"><SelectValue placeholder="Choose" /></SelectTrigger>
                  <SelectContent>
                    {PRESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Manufacturer</label>
                <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} className="rounded-sm" placeholder="e.g. Mark Andy" />
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Model</label>
              <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-sm" placeholder="e.g. Performance P7" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Web (in)</label>
                <Input type="number" step="0.1" value={form.web_width_inches} onChange={(e) => setForm({ ...form, web_width_inches: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Max (fpm)</label>
                <Input type="number" value={form.max_speed_fpm} onChange={(e) => setForm({ ...form, max_speed_fpm: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Stations</label>
                <Input type="number" value={form.stations} onChange={(e) => setForm({ ...form, stations: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Recommended ink systems (comma-separated)</label>
              <Input value={form.recommended_ink_systems} onChange={(e) => setForm({ ...form, recommended_ink_systems: e.target.value })} className="rounded-sm" placeholder="e.g. Water-based flexo, UV flexo" />
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Notes</label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm resize-none" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-press-btn">
              {loading ? "Saving…" : "Save press"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
